import React, { useState, FC, MouseEvent } from "react";

import { QueryObserverResult } from "@tanstack/react-query";
import { toast } from "react-toastify";

import AiIcon from "~/assets/images/ai_icon/kagamin_big_smile.png";

import {
  useApisMembersEmployeeSurveySubmissionsEmployeeSurveySubmissionRepliesCreate,
  useEditorState,
  useEditorStateLinkContentGetter,
  useApisMembersEmployeeSurveySubmissionsAiResponsesShow,
  useProvidersCurrentEmployee,
  useClipboard,
} from "~/hooks";

import { DotLoadingAnimation, PostDetailWrapper } from "~/components/atoms";
import {
  EmployeeSurveySubmissionReplyDetailWithEditForm,
  EmployeeSurveySubmissionDetail,
  TextEditor,
  PostHeader,
} from "~/components/organisms";

import {
  EmployeeSurveySubmissionType,
  EmployeeSurveySubmissionReplyType,
  MentionSuggestEmployeeType,
  PreviewWithFileType,
  ApiMemberEmployeeSurveySubmissionRepliesIndexResponseType,
  PostDetailMenuItemType,
} from "~/domains";

import { Href } from "~/constants/postUrl";

type PropsType = {
  highlightEmployeeSurveySubmissionReplyId?: string;
  employeeSurveySubmission: EmployeeSurveySubmissionType;
  employeeSurveySubmissionReplies: EmployeeSurveySubmissionReplyType[];
  mentions: MentionSuggestEmployeeType[];
  className?: string;
  refetch: () => Promise<
    QueryObserverResult<
      ApiMemberEmployeeSurveySubmissionRepliesIndexResponseType,
      unknown
    >
  >;
};

export const EmployeeSurveySubmissionRepliesWithEmployeeSurveySubmission: FC<
  PropsType
> = ({
  highlightEmployeeSurveySubmissionReplyId = "",
  employeeSurveySubmission,
  employeeSurveySubmissionReplies,
  mentions,
  className = "",
  refetch,
}: PropsType) => {
  const { clipboardToCopy } = useClipboard();
  const { currentEmployee } = useProvidersCurrentEmployee();
  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const [
    {
      value: editorState,
      onChange: setEditorState,
      jsonContent,
      hasText,
      setPlainText,
    },
  ] = useEditorState();
  const { fetchedData } = useEditorStateLinkContentGetter({
    watchEditorState: editorState,
    isWatch: true,
  });

  const { mutate, isLoading } =
    useApisMembersEmployeeSurveySubmissionsEmployeeSurveySubmissionRepliesCreate();

  const shouldAiFeedback =
    employeeSurveySubmission.employeeSurvey.shouldAiFeedback;
  const isMyEmployeeSurveySubmission =
    employeeSurveySubmission.employee.id === currentEmployee?.id;

  const { data: employeeSurveySubmissionsAiResponseData, isFetching } =
    useApisMembersEmployeeSurveySubmissionsAiResponsesShow({
      employeeSurveySubmissionId: employeeSurveySubmission.id,
    });

  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!hasText) {
      toast.error("テキストが空です");
      return;
    }
    const body = {
      content: jsonContent,
      attachFiles: files,
      quotePostId: fetchedData?.id,
    };

    mutate(
      {
        employeeSurveySubmissionId: employeeSurveySubmission.id,
        body,
      },
      {
        onSuccess: async () => {
          setPlainText("");
          setFiles([]);
          await refetch();
        },
      },
    );
  };

  const detailItems: PostDetailMenuItemType[] = [
    {
      icon: "ioCopyOutline",
      text: "リンクをコピー",
      onClick: () =>
        clipboardToCopy(
          `${location.origin}${Href.EmployeeSurveySubmission(employeeSurveySubmission.id)}`,
        ),
      disabled: false,
    },
  ];

  return (
    <div className={className}>
      <div className="space-y-6">
        <EmployeeSurveySubmissionDetail
          employeeSurveySubmission={employeeSurveySubmission}
          openReply={() => refetch()}
          canDelete={false}
        />
        {!isFetching &&
          employeeSurveySubmissionsAiResponseData?.employeeSurveySubmissionAiResponse && (
            <PostDetailWrapper isHighlight={false}>
              <PostHeader
                miniText={
                  employeeSurveySubmissionsAiResponseData
                    ?.employeeSurveySubmissionAiResponse.createdAt
                }
                postedEmployee={{
                  id: "",
                  name: "かがみんAI",
                  avatarUrl: AiIcon as string,
                }}
                menuItems={detailItems}
              />
              <div className="mt-2 whitespace-pre-wrap pl-16">
                {
                  employeeSurveySubmissionsAiResponseData
                    .employeeSurveySubmissionAiResponse?.answer
                }
              </div>
            </PostDetailWrapper>
          )}
        {shouldAiFeedback &&
          isMyEmployeeSurveySubmission &&
          !isFetching &&
          !employeeSurveySubmissionsAiResponseData?.employeeSurveySubmissionAiResponse && (
            <DotLoadingAnimation className="justify-center" />
          )}
        <div>
          {employeeSurveySubmissionReplies.map((reply) => (
            <EmployeeSurveySubmissionReplyDetailWithEditForm
              key={reply.id}
              employeeSurveySubmissionReply={reply}
              mentions={mentions}
              refetch={refetch}
              isHighlight={
                reply.id === highlightEmployeeSurveySubmissionReplyId
              }
            />
          ))}
        </div>
        <TextEditor
          submitButton={{
            isSubmitting: isLoading,
            onSubmit: handleSubmit,
          }}
          mentions={mentions}
          editorState={editorState}
          setEditorState={setEditorState}
          files={files}
          onChangeFiles={setFiles}
          blockQuote={fetchedData}
          isEmojiPickerTop
        />
      </div>
    </div>
  );
};
